import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Clock } from 'lucide-react';
import { categoryIcons, getIcon } from '../../utils/categoryIcons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeOptions = [];
for (let h = 0; h < 24; h++) {
  timeOptions.push(`${h.toString().padStart(2, '0')}:00`);
  timeOptions.push(`${h.toString().padStart(2, '0')}:30`);
}

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteCategory, setDeleteCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    icon: 'Package',
    sort_order: '0',
    display_group: '',
    time_start: '',
    time_end: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load categories');
    }
  };

  const openModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        icon: category.icon,
        sort_order: category.sort_order.toString(),
        display_group: category.display_group || '',
        time_start: category.time_start || '',
        time_end: category.time_end || ''
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        icon: 'Package',
        sort_order: (categories.length + 1).toString(),
        display_group: '',
        time_start: '',
        time_end: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      name: formData.name,
      icon: formData.icon,
      sort_order: parseInt(formData.sort_order),
      display_group: formData.display_group || null,
      time_start: formData.time_start || null,
      time_end: formData.time_end || null
    };

    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, data);
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/categories`, data);
        toast.success('Category created');
      }
      setIsModalOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleDelete = async () => {
    if (!deleteCategory) return;
    
    try {
      await axios.delete(`${API}/categories/${deleteCategory.id}`);
      toast.success('Category deleted');
      setDeleteCategory(null);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  return (
    <div data-testid="categories-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Categories</h1>
          <p className="text-slate-500 mt-1 font-body">Organize your products into categories</p>
        </div>
        <Button data-testid="add-category-btn" onClick={() => openModal()}>
          <Plus className="w-4 h-4 mr-2" />
          Add Category
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((category) => {
          const IconComponent = getIcon(category.icon);
          return (
            <div
              key={category.id}
              data-testid={`category-card-${category.id}`}
              className="bg-white rounded-lg border border-slate-200 p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                    <IconComponent className="w-6 h-6 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900">{category.name}</h3>
                    <p className="text-sm text-slate-500">Order: {category.sort_order}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`edit-category-${category.id}`}
                    onClick={() => openModal(category)}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`delete-category-${category.id}`}
                    onClick={() => setDeleteCategory(category)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Time slot and group info */}
              <div className="mt-3 pt-3 border-t border-slate-100 space-y-1">
                {category.display_group && (
                  <p className="text-xs text-purple-600">
                    Group: {category.display_group}
                  </p>
                )}
                {category.time_start && category.time_end && (
                  <p className="text-xs text-blue-600 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {category.time_start} - {category.time_end}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {categories.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          No categories yet. Add your first category to get started.
        </div>
      )}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent data-testid="category-modal" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingCategory ? 'Edit Category' : 'Add Category'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {editingCategory ? 'Edit category details' : 'Create a new category'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Category Name</Label>
              <Input
                id="name"
                data-testid="category-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="icon">Icon</Label>
                <Select
                  value={formData.icon}
                  onValueChange={(value) => setFormData({ ...formData, icon: value })}
                >
                  <SelectTrigger data-testid="category-icon-select">
                    <SelectValue placeholder="Select icon" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {categoryIcons.map((option) => {
                      const IconComp = option.icon;
                      return (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <IconComp className="w-4 h-4" />
                            {option.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sort_order">Sort Order</Label>
                <Input
                  id="sort_order"
                  data-testid="category-order-input"
                  type="number"
                  min="0"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="display_group">Display Group (optional)</Label>
              <Input
                id="display_group"
                data-testid="category-group-input"
                value={formData.display_group}
                onChange={(e) => setFormData({ ...formData, display_group: e.target.value })}
                placeholder="e.g., lunch (to combine with other categories)"
              />
              <p className="text-xs text-slate-500">
                Categories with the same display group will appear together on the till
              </p>
            </div>

            <div className="p-3 bg-slate-50 rounded-lg space-y-3">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Time Slot (optional)
              </Label>
              <p className="text-xs text-slate-500">
                Auto-select this category during specified times
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Start Time</Label>
                  <Select
                    value={formData.time_start || "none"}
                    onValueChange={(v) => setFormData({ ...formData, time_start: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="category-time-start">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {timeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">End Time</Label>
                  <Select
                    value={formData.time_end || "none"}
                    onValueChange={(v) => setFormData({ ...formData, time_end: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="category-time-end">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {timeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-category-btn">
                {editingCategory ? 'Save Changes' : 'Add Category'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteCategory} onOpenChange={() => setDeleteCategory(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteCategory?.name}"? 
              This cannot be undone. Categories with active products cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-category"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CategoriesPage;
